import * as React from "react"
import { ChevronDown, Check } from "lucide-react"
import { cn } from "../../lib/utils"

type SelectProps = {
  value: string
  onValueChange: (value: string) => void
  children: React.ReactNode
  placeholder?: string
  className?: string
}

export function Select({ value, onValueChange, children, placeholder, className }: SelectProps) {
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  React.useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (
        triggerRef.current && 
        !triggerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handle)
    return () => document.removeEventListener("mousedown", handle)
  }, [open])

  // Find selected label
  let selectedLabel = placeholder || "Select..."
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.props.value === value) {
      selectedLabel = child.props.children
    }
  })

  const handleOptionClick = (optionValue: string) => {
    onValueChange(optionValue)
    setOpen(false)
  }

  return (
    <div className={cn("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        className="flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="block truncate">{selectedLabel}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
      {open && (
        <div ref={dropdownRef} className="absolute top-full left-0 z-[9999] mt-1 w-full rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) return null
            const isSelected = child.props.value === value
            return (
              <div
                key={child.props.value}
                onClick={() => handleOptionClick(child.props.value)}
                className={cn(
                  "relative flex cursor-pointer select-none items-center py-2 px-3 text-sm outline-none hover:bg-slate-100 focus:bg-slate-100",
                  isSelected && "bg-slate-100 font-semibold"
                )}
              >
                {child.props.children}
                {isSelected && <Check className="absolute right-3 h-4 w-4" />}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

Select.Option = function Option({ value, children }: { value: string; children: React.ReactNode }) {
  return <div data-value={value}>{children}</div>
}
